import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  buildOpportunityPayload,
  entryPointConfigs,
  type EntryPointConfig,
  type OpportunityType,
} from "@/lib/opportunityPayload";
import {
  asSubmittedResponse,
  submitOperatorOpportunity,
  type OperatorIntakeResult,
} from "@/lib/operatorIntake";
import { trackPublicFunnelEvent } from "@/lib/publicFunnel";

type EntryPointKey = keyof typeof entryPointConfigs;

interface UpdatesTrack {
  configKey: EntryPointKey;
  label: string;
  contentTrack: string;
  defaultContext: string;
}

interface UpdatesSignupProps {
  placement: string;
  title?: string;
  description?: string;
  defaultTopic?: UpdatesTrackKey;
}

export interface UpdatesSignupCardProps {
  placement: string;
  defaultTopic?: UpdatesTrackKey;
  variant?: "section" | "modal";
  submitLabel?: string;
  successTitle?: string;
  onSubmitted?: (result: OperatorIntakeResult) => void;
}

const updatesTracks = {
  general: {
    configKey: "general",
    label: "General SVM updates",
    contentTrack: "SVM launch notes",
    defaultContext: "Wants general SVM updates about apps, operators, systems, and launch decisions.",
  },
  appRescue: {
    configKey: "appRescue",
    label: "App Rescue notes",
    contentTrack: "App Rescue launch notes",
    defaultContext: "Wants App Rescue notes and diagnostic booking updates.",
  },
  appBuild: {
    configKey: "appBuild",
    label: "App build notes",
    contentTrack: "app build launch notes",
    defaultContext: "Wants app-build launch notes and first-release strategy.",
  },
  aiOperator: {
    configKey: "aiOperator",
    label: "SVM Operator field notes",
    contentTrack: "SVM Operator field notes",
    defaultContext: "Wants practical notes about organizing intake, decisions, follow-up, and delivery.",
  },
  boardroom: {
    configKey: "general",
    label: "AI Boardroom private beta",
    contentTrack: "AI Boardroom private beta",
    defaultContext: "Wants early access and practical notes about the AI Boardroom private beta.",
  },
  systemsAudit: {
    configKey: "systemsAudit",
    label: "Systems audit checklist",
    contentTrack: "systems audit checklist",
    defaultContext: "Wants systems-audit notes about leaks, repeated work, and missing ownership.",
  },
  ventureEvaluation: {
    configKey: "ventureEvaluation",
    label: "Venture evaluation notes",
    contentTrack: "venture evaluation notes",
    defaultContext: "Wants decision notes for domains, ventures, partnerships, assets, or deals.",
  },
} satisfies Record<string, UpdatesTrack>;

export type UpdatesTrackKey = keyof typeof updatesTracks;

const orderedTrackKeys: UpdatesTrackKey[] = [
  "general",
  "appRescue",
  "appBuild",
  "aiOperator",
  "boardroom",
  "systemsAudit",
  "ventureEvaluation",
];

function newSubmissionId(): string {
  const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return `updates-${cryptoObj.randomUUID()}`;
  }
  return `updates-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function safeId(value: string): string {
  return value.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "svm-updates";
}

function trackService(config: EntryPointConfig): OpportunityType {
  return config.opportunityType;
}

export const UpdatesSignupCard = ({
  placement,
  defaultTopic = "general",
  variant = "section",
  submitLabel = "Keep me posted",
  successTitle = "You are on the SVM updates list.",
  onSubmitted,
}: UpdatesSignupCardProps) => {
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState<UpdatesTrackKey>(defaultTopic);
  const [consented, setConsented] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<OperatorIntakeResult | null>(null);
  const submissionId = useRef(newSubmissionId());
  const hasStarted = useRef(false);
  const signupId = useMemo(() => `${safeId(placement)}-updates-signup`, [placement]);
  const selectedTrack = updatesTracks[topic];
  const config = entryPointConfigs[selectedTrack.configKey];
  const submittedResponse = result?.mode === "submitted" ? asSubmittedResponse(result.response) : null;
  const isModal = variant === "modal";
  const analyticsSignupType = "updates";

  useEffect(() => {
    if (!result) {
      setTopic(defaultTopic);
    }
  }, [defaultTopic, result]);

  const trackSignupEvent = (name: string, extra: Record<string, string | number | boolean | undefined> = {}) => {
    trackPublicFunnelEvent(name, {
      placement,
      route: config.path,
      service: trackService(config),
      entry_point: config.entryPoint,
      updates_topic: topic,
      offer_track: selectedTrack.contentTrack,
      signup_type: analyticsSignupType,
      subscription_intent: "general_updates",
      ...extra,
    });
  };

  const markStarted = () => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    trackSignupEvent("updates_signup_started");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isValidEmail(email)) {
      setError("Enter a valid email so SVM can send the updates.");
      return;
    }

    if (!consented) {
      setError("Confirm that SVM may send you useful updates before joining.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const payload = buildOpportunityPayload({
      config,
      form: {
        name: "SVM Updates Subscriber",
        email: email.trim(),
        summary: `${selectedTrack.label} signup from ${placement}. ${selectedTrack.defaultContext}`,
        desiredOutcome: `Receive ${selectedTrack.contentTrack} and related SVM launch updates.`,
        timeline: "Keep me posted",
        budgetRange: "Not sure yet",
        urgency: "Low - information gathering",
        topic: selectedTrack.label,
      },
      details: {
        signupType: "offer_interest",
        analyticsSignupType,
        newsletterInterest: "true",
        subscriptionIntent: "general_updates",
        lifecycleStage: "nurture",
        leadIntent: "keep_me_updated",
        offerTitle: config.title,
        offerPath: config.path,
        offerTrack: selectedTrack.contentTrack,
        updatesTopic: topic,
        signupPlacement: placement,
        visitorContext: selectedTrack.defaultContext,
      },
      submissionId: submissionId.current,
    });

    trackSignupEvent("updates_signup_attempted", {
      consented,
    });

    try {
      const nextResult = await submitOperatorOpportunity(payload);
      const submitted = nextResult.mode === "submitted" ? asSubmittedResponse(nextResult.response) : null;

      setResult(nextResult);
      onSubmitted?.(nextResult);
      trackSignupEvent("updates_signup_submitted", {
        mode: nextResult.mode,
        duplicate: submitted?.duplicate,
        operator_status: submitted?.status,
        has_reference: Boolean(submitted?.opportunity_id),
      });
    } catch (caught) {
      trackSignupEvent("updates_signup_failed", { reason: "submit_error" });
      setError(caught instanceof Error ? caught.message : "We could not add you right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={
        isModal
          ? "rounded-md border border-slate-200 bg-white p-4 text-slate-950"
          : "rounded-md border border-white/15 bg-white p-4 text-slate-950 shadow-[0_24px_70px_rgba(0,0,0,0.22)] md:p-5"
      }
    >
          {result ? (
            <div aria-live="polite" className={`grid gap-4 ${isModal ? "min-h-32" : "min-h-40"}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
                  {result.mode === "submitted" ? "Signup received" : "Signup preview ready"}
                </p>
                <h3 className="text-2xl font-black text-slate-950">{successTitle}</h3>
                <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
                  {result.mode === "submitted"
                    ? `We will send ${selectedTrack.contentTrack.toLowerCase()} and useful launch updates as they are ready.`
                    : result.message}
                </p>
                {submittedResponse?.opportunity_id && (
                  <p className="mt-3 text-xs font-bold text-slate-500">Reference: {submittedResponse.opportunity_id}</p>
                )}
              </div>
            </div>
          ) : (
            <form
              className={
                isModal
                  ? "grid gap-4"
                  : "grid gap-4 md:grid-cols-[1fr_1.1fr_auto] md:items-end"
              }
              onSubmit={handleSubmit}
              noValidate
            >
              <label htmlFor={`${signupId}-topic`} className="grid gap-2 text-sm">
                <span className="font-bold text-slate-950">What should SVM send you?</span>
                <select
                  id={`${signupId}-topic`}
                  value={topic}
                  onFocus={markStarted}
                  onChange={(event) => {
                    const nextTopic = event.target.value as UpdatesTrackKey;
                    setTopic(nextTopic);
                    trackPublicFunnelEvent("updates_topic_selected", {
                      placement,
                      updates_topic: nextTopic,
                      offer_track: updatesTracks[nextTopic].contentTrack,
                      signup_type: analyticsSignupType,
                      subscription_intent: "general_updates",
                    });
                  }}
                  className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-950 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-600"
                >
                  {orderedTrackKeys.map((trackKey) => (
                    <option key={trackKey} value={trackKey}>
                      {updatesTracks[trackKey].label}
                    </option>
                  ))}
                </select>
              </label>

              <label htmlFor={`${signupId}-email`} className="grid gap-2 text-sm">
                <span className="font-bold text-slate-950">Email</span>
                <Input
                  id={`${signupId}-email`}
                  type="email"
                  autoComplete="email"
                  value={email}
                  placeholder="you@company.com"
                  onFocus={markStarted}
                  onChange={(event) => {
                    setError("");
                    setEmail(event.target.value);
                  }}
                />
              </label>

              <Button
                type="submit"
                disabled={isSubmitting || !consented}
                className={`h-11 rounded-md bg-slate-950 px-5 font-bold text-white hover:bg-rose-700 disabled:opacity-60 ${isModal ? "w-full" : ""}`}
              >
                <Send className="h-4 w-4" />
                {isSubmitting ? "Joining..." : submitLabel}
              </Button>

              <label
                className={`flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs font-medium leading-5 text-slate-700 ${isModal ? "" : "md:col-span-3"}`}
              >
                <input
                  type="checkbox"
                  required
                  checked={consented}
                  onChange={(event) => {
                    markStarted();
                    setError("");
                    setConsented(event.target.checked);
                  }}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-400 text-rose-600 focus:ring-rose-500"
                />
                <span>
                  Send me useful SVM notes and launch updates. I understand this is an information track, not a request
                  for passwords, credentials, or private records.
                </span>
              </label>

              {error && (
                <p
                  role="alert"
                  className={`rounded-md border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-800 ${isModal ? "" : "md:col-span-3"}`}
                >
                  {error}
                </p>
              )}
            </form>
          )}

          <div className="mt-4 grid gap-2 border-t border-slate-200 pt-4 text-xs font-semibold leading-5 text-slate-600 sm:grid-cols-3">
            {["Pick a track", "Leave only your email", "SVM routes it to nurture"].map((item) => (
              <div key={item} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" />
                <span>{item}</span>
              </div>
            ))}
          </div>
    </div>
  );
};

const UpdatesSignup = ({
  placement,
  title = "Want the notes before you need the form?",
  description = "Join the lightweight SVM updates list. Pick the track you care about and we will send useful context as the public launch material gets sharper.",
  defaultTopic = "general",
}: UpdatesSignupProps) => {
  return (
    <section id={`${safeId(placement)}-updates-signup`} className="border-y border-slate-300 bg-[#0b0d10] py-10 text-white md:py-12">
      <div className="container mx-auto grid gap-7 px-4 lg:grid-cols-[0.86fr_1.14fr] lg:items-center lg:px-8">
        <div>
          <p className="mb-3 inline-flex rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-amber-300">
            Keep up with SVM
          </p>
          <h2 className="svm-display-tight max-w-3xl text-4xl leading-tight md:text-5xl">{title}</h2>
          <p className="mt-4 max-w-2xl font-medium leading-7 text-slate-300">{description}</p>
          <div className="mt-5 flex flex-wrap gap-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
            <span>No credentials</span>
            <span className="text-amber-300">/</span>
            <span>Useful notes</span>
            <span className="text-amber-300">/</span>
            <span>Lower-pressure follow-up</span>
          </div>
        </div>

        <UpdatesSignupCard placement={placement} defaultTopic={defaultTopic} />
      </div>
    </section>
  );
};

export default UpdatesSignup;
